import React, {
  ComponentType,
  useCallback,
} from 'react';
import set from 'lodash/set';
import isNil from 'lodash/isNil';
import merge from 'lodash/merge';
import cloneDeep from 'lodash/cloneDeep';
import clsx from 'clsx';
import {
  useFieldArray,
  useFormContext,
} from 'react-hook-form';
import AddIcon from '@mui/icons-material/Add';
import Button from 'pages/Dashboard/components/Button';
import ToolTip from 'core/components/Tooltip';
import {
  PatientForm,
} from 'pages/Dashboard/types/patient';

type ComponentParts = 'root' | 'field' | 'add' | 'title';

export type FieldComponentProps = {
  index: number;
  isLast: boolean;
  showLabel: boolean;
  onDelete: (index: number) => void;
  update?: (index: number, object: Record<string, any>) => void;
  append: () => void;
  prefix?: string;
  initialValue: Record<string, any>;
  value: Record<string, any>;
  patient?: PatientForm;
};

type ActionProps = {
  text?: string;
  color?: 'primary' | 'error' | 'inherit' | 'secondary';
  variant?: 'text' | 'outlined' | 'contained';
  tooltip?: string;
};

export type Props = {
  title?: string;
  formKey: string;
  FieldComponent: ComponentType<FieldComponentProps>;
  getDefaultValue?: (
    fields: Record<string, string>[],
    action: 'delete' | 'append',
  ) => Record<string, any>;
  append?: ActionProps | null;
  className?: string;
  fieldContainerClassName?: string;
  titleClassName?: string;
  keepOne?: boolean;
  maxItems?: number;
  patient?: PatientForm;
  classes?: Partial<Record<ComponentParts, string>>;
  shouldValidate?: boolean;
  observeValues?: boolean;
};

const defaultAppendActionProps = {
  text: 'Add',
  variant: 'text',
  color: 'primary',
};

export default function FieldsArrayWrapper({
  title,
  formKey,
  FieldComponent,
  getDefaultValue = () => ({}),
  append: _appendProps,
  className,
  fieldContainerClassName,
  titleClassName,
  keepOne = false,
  maxItems,
  patient,
  classes,
  shouldValidate = true,
  observeValues = true,
}: Props) {
  const {
    control,
    trigger,
    formState,
    watch,
  } = useFormContext();
  const {
    fields,
    append,
    remove,
    update,
  } = useFieldArray({ control, name: formKey });

  const values = observeValues ? watch(formKey) : undefined;
  const hasAppend = !isNil(_appendProps);
  const appendProps = hasAppend ? merge({ ...defaultAppendActionProps }, _appendProps) : null;
  const isMaxItemsReached = !isNil(maxItems) && fields.length >= maxItems;

  const onDelete = useCallback((index: number) => {
    if (!keepOne || fields.length > 1) {
      remove(index);
    } else if (keepOne) {
      const defaultRecordValue = getDefaultValue(fields, 'delete');
      update(index, { ...defaultRecordValue });
    }

    // @TODO: need a fix
    // formState has to be immutable
    // this is not a proper way to set field as touched
    set(formState.touchedFields, formKey, true);
  }, [fields, keepOne]);

  const handleAppend = async () => {
    if (!isMaxItemsReached) {
      let isValid = true;
      if (shouldValidate) {
        isValid = await trigger(formKey, { shouldFocus: true });
      }

      if (isValid) {
        const defaultRecordValue = getDefaultValue(fields, 'append');
        append(cloneDeep(defaultRecordValue));
      }
    }
  };

  return (
    <div className={clsx(className, classes?.root)}>
      {!isNil(title) && (
        <h3 className={clsx(
          'text-base font-semibold my-4',
          titleClassName,
          classes?.title,
        )}
        >
          {title}
        </h3>
      )}
      {fields.length > 0 && (
        <div className={clsx(
          'space-y-2.5 mb-6 w-4/5 small:w-full',
          fieldContainerClassName,
          classes?.field,
        )}
        >
          {fields.map((field: Record<'id', string>, index: number) => (
            <FieldComponent
              key={field.id}
              showLabel={index === 0}
              index={index}
              onDelete={onDelete}
              update={update}
              prefix={formKey}
              initialValue={field}
              value={observeValues ? values?.[index] : field}
              patient={patient}
              append={handleAppend}
              isLast={index === (fields.length - 1)}
            />
          ))}
        </div>
      )}
      {hasAppend && (
        <ToolTip
          title={appendProps?.tooltip ?? 'Maximum items reached'}
          hide={!isMaxItemsReached}
        >
          <span className="inline-block">
            <Button
              {...appendProps!}
              className={clsx('!text-xs !font-medium', classes?.add)}
              onClick={handleAppend}
              disabled={isMaxItemsReached}
            >
              <AddIcon
                className="mr-1"
                fontSize="small"
              />
              {appendProps!.text}
            </Button>
          </span>
        </ToolTip>
      )}
    </div>
  );
}
